import { Stack, useDisclosure } from '@chakra-ui/react';
import MainLayout from 'components/layouts/MainLayout';
import { StackedBanner as FluctBanner } from 'features/fluct';
import CuminBarcodeBox from 'features/membership/components/cumin/CuminBarcodeBox';
import CuminOvertimePointGetCautionModal from 'features/membership/components/cumin/CuminOvertimePointGetCautionModal';
import GingerPointBox from 'features/membership/components/ginger/GingerPointBox';
import GingerPrepaidAccordion from 'features/membership/components/ginger/GingerPrepaidAccordion';
import usePointBalance from 'features/membership/hooks/ginger/usePointBalance';
import usePrepaidBalance from 'features/membership/hooks/ginger/usePrepaidBalance';
import { TEMPLATE_ALIAS_CODE } from 'features/membership/libs/const/aliasCode';
import { EARTH_FEATURE_CODES } from 'features/membership/libs/const/plugins/earthFeatureCodes';
import { JUPITER_FEATURE_CODES } from 'features/membership/libs/const/plugins/jupiterFeatureCodes';
import { PA2_FEATURE_CODES } from 'features/membership/libs/const/plugins/pa2FeatureCodes';
import {
  GingerPagePageTemplateOptionKey,
  membershipPluginFeatureState,
  membershipPluginPageTemplateOptionState
} from 'features/membership/libs/recoil/selector';
import { PageTemplateOption } from 'features/membership/types';
import { TopBanner as SmartReceiptTopBanner } from 'features/smart-receipt/components';
import { FEATURES } from 'libs/features';
import {
  membershipMemberResponseAtom,
  tenantConfigurationAtom
} from 'libs/recoil/atom';
import _ from 'lodash';
import { FC } from 'react';
import { useEffectOnce } from 'react-use';
import { useRecoilValue } from 'recoil';

/**
 * VD+CSD トップ
 */
const GingerTop: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const membershipMemberResponse = useRecoilValue(membershipMemberResponseAtom);
  const tenantConfig = useRecoilValue(tenantConfigurationAtom);
  const plugins = useRecoilValue(membershipPluginFeatureState);
  const jupiterFeatureCodes = plugins.jupiter;
  const earthFeatureCodes = plugins.earth;
  const pa2FeatureCodes = plugins.pa2;

  const isGetPrepaidBalanceFeatureActive = Boolean(
    jupiterFeatureCodes?.includes(
      _.camelCase(JUPITER_FEATURE_CODES.GET_BALANCE)
    )
  );

  const isGetTransactionHistoriesFeatureActive = Boolean(
    jupiterFeatureCodes?.includes(
      _.camelCase(JUPITER_FEATURE_CODES.GET_TRANSACTION_HISTORIES)
    )
  );

  // PA2 切替時は earth がOFF / pa2 がON、移行前は earth がON のため、いずれかが有効ならポイント表示する
  const isGetPointBalanceFeatureActive = Boolean(
    earthFeatureCodes?.includes(
      _.camelCase(EARTH_FEATURE_CODES.GET_POINT_BALANCE)
    ) ||
      pa2FeatureCodes?.includes(
        _.camelCase(PA2_FEATURE_CODES.GET_POINT_BALANCE)
      )
  );

  const templateOptions = useRecoilValue<
    Record<GingerPagePageTemplateOptionKey, PageTemplateOption> | undefined
  >(membershipPluginPageTemplateOptionState(TEMPLATE_ALIAS_CODE.GINGER));
  // 会員番号
  const cardNumber =
    membershipMemberResponse && membershipMemberResponse.status === 'succeeded'
      ? membershipMemberResponse.data.card_number
      : '';

  const {
    pointBalanceResponseData,
    serviceTime,
    isMutating: isPointBalanceMutating,
    executeGetPointBalance
  } = usePointBalance(cardNumber, onOpen);

  const {
    prepaidBalanceResponseData,
    isMutating: isPrepaidBalanceMutating,
    executeGetPrepaidBalance
  } = usePrepaidBalance();

  const updateButtonClickHandler = async () => {
    await executeGetPointBalance();
  };

  // アコーディオンの開閉ハンドラ
  const handleAccordionToggle = (expandedIndex: number) => {
    if (expandedIndex === 0) {
      // アコーディオンが開かれた場合のみAPIを呼び出す
      executeGetPrepaidBalance();
    }
  };

  // 初回実行
  useEffectOnce(() => {
    executeGetPointBalance();
  });

  return (
    <MainLayout header footer bg="dark.50">
      <Stack px="1rem" pt="3rem" spacing="1.25rem">
        <CuminBarcodeBox
          cardNumber={cardNumber}
          imageUrl={templateOptions?.topImageOption?.image_url}
          barcodeFormat={
            templateOptions?.topBarcodeTypeOption?.content ?? 'EAN13'
          }
        />
        {/* ハウスマネー表示欄 */}
        {isGetPrepaidBalanceFeatureActive && (
          <GingerPrepaidAccordion
            prepaidDisplay={{
              prepaidName:
                templateOptions?.houseMoneyNameContentOption?.content,
              prepaidBalance: prepaidBalanceResponseData?.balance_total,
              prepaidUnit: templateOptions?.houseMoneyUnitContentOption?.content
            }}
            isPrepaidMutating={isPrepaidBalanceMutating}
            onToggle={handleAccordionToggle}
            onUpdateClick={() => {
              executeGetPrepaidBalance();
            }}
            isUpdateLoading={isPrepaidBalanceMutating}
            showHistoryButton={isGetTransactionHistoriesFeatureActive}
            historyPath={`${FEATURES.membership.path}/${TEMPLATE_ALIAS_CODE.GINGER}/transaction-histories`}
          />
        )}
        {/* CSDポイント表示欄 */}
        {isGetPointBalanceFeatureActive && (
          <>
            <GingerPointBox
              pointDisplay={{
                pointName: templateOptions?.pointNameContentOption?.content,
                pointBalance: pointBalanceResponseData?.total_point,
                pointUnit: templateOptions?.pointUnitContentOption?.content,
                unavailableHour: serviceTime?.unavailable_hour
              }}
              expiredDateDisplay={{
                pointExpiredDate: pointBalanceResponseData?.lapse_due_date,
                showPointExpiredDate: Boolean(
                  templateOptions?.topPointExpiryDateVisibleOption
                )
              }}
              isCardPointMutating={isPointBalanceMutating}
              onUpdateButtonClick={updateButtonClickHandler}
            />
            {/* NOTE: onOpenをuseCardPointsの引数に代入する関係で、GingerPointBoxの中には実装しない */}
            <CuminOvertimePointGetCautionModal
              isOpen={isOpen}
              onClose={onClose}
              isLoaded={!isPointBalanceMutating}
              availableHour={serviceTime?.available_hour}
            />
          </>
        )}
      </Stack>
      <FluctBanner
        pageType="member_card_top"
        allowPopup={true}
        ppid={cardNumber}
      />
      {tenantConfig.smart_receipt?.enabled && <SmartReceiptTopBanner />}
    </MainLayout>
  );
};

export default GingerTop;
